'use client';

import { useRef, useEffect, useState, type CSSProperties } from 'react';
import Link from 'next/link';
import { gsap } from '@/lib/gsap';
import { getActiveClients } from '@/lib/services/clientService';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { aboutCopy } from '@/lib/i18n/pages/about';

const ARABIC_SCRIPT = /[\u0600-\u06FF]/;

export function AboutPageClient() {
  const { locale, dir, href } = useLocale();
  const isRtl = dir === 'rtl';
  const t = pageCopy(aboutCopy, locale);
  const services = t.services.items;
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const contactTitleRef = useRef<HTMLHeadingElement>(null);
  const missionRef = useRef<HTMLDivElement>(null);
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const [clients, setClients] = useState<{ name: string; url: string | null }[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);

  // Fetch clients from Firestore
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const firestoreClients = await getActiveClients();
        setClients(firestoreClients.map((c) => ({
          name: c.name,
          url: c.url || null,
        })));
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, []);

  // Toggle service accordion
  const toggleService = (index: number) => {
    setExpandedService(expandedService === index ? null : index);
  };

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    if (!heroRef.current) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const ctx = gsap.context(() => {
      // Hero is rendered visible by default for LCP. Animate from a near-visible
      // state so the heading is always paintable on first frame.
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters && !prefersReducedMotion) {
        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);
        shuffledLetters.forEach((letter, index) => {
          gsap.fromTo(
            letter,
            { scaleY: 0.5 },
            { duration: 0.5, scaleY: 1, ease: 'power3.out', delay: index * 0.04 },
          );
        });
        setTimeout(() => {
          applyHoverEffect(letters);
        }, 1200);
      } else if (letters) {
        applyHoverEffect(letters);
      }

      // Subtitle visible by default; only a small slide-in enhancement
      if (subtitleRef.current && !prefersReducedMotion) {
        gsap.fromTo(
          subtitleRef.current,
          { y: 16, opacity: 0.4 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
        );
      }

      // Animate services on scroll
      if (servicesRef.current) {
        const serviceItems = servicesRef.current.querySelectorAll('.service-item');
        serviceItems.forEach((item, index) => {
          gsap.set(item, { y: 40, opacity: 0 });
          gsap.to(item, {
            scrollTrigger: {
              trigger: item,
              start: 'top 88%',
            },
            y: 0,
            opacity: 1,
            duration: 0.5,
            delay: index * 0.03,
            ease: 'power3.out',
          });
        });
      }

      // Animate mission text
      if (missionRef.current) {
        gsap.set(missionRef.current, { y: 40, opacity: 0 });
        gsap.to(missionRef.current, {
          scrollTrigger: {
            trigger: missionRef.current,
            start: 'top 80%',
          },
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
        });
      }

      // Animate CONTACT letters on scroll
      const contactLetters = contactTitleRef.current?.querySelectorAll('.contact-letter');
      if (contactLetters) {
        gsap.set(contactLetters, { scaleY: 0.3, opacity: 0 });

        gsap.to(contactLetters, {
          scrollTrigger: {
            trigger: contactTitleRef.current,
            start: 'top 80%',
          },
          scaleY: 1,
          opacity: 1,
          duration: 0.6,
          stagger: {
            each: 0.05,
            from: 'random',
          },
          ease: 'power3.out',
          onComplete: () => {
            applyHoverEffect(contactLetters);
          },
        });
      }
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const title = t.hero.title;

  // Client names come from Firestore and are usually Latin brand names, which
  // keep the wide-tracked caps look. Arabic-script names must not be spaced.
  const clientNameStyle = (name: string): CSSProperties =>
    ARABIC_SCRIPT.test(name)
      ? { letterSpacing: 'normal', textTransform: 'none', textAlign: 'center' }
      : { letterSpacing: '0.15em', ...(isRtl ? { textAlign: 'center' as const } : {}) };

  // Micro labels use wide tracking + uppercase in English; both break Arabic
  // script joining, so RTL gets a plain, slightly larger label instead.
  const microLabel: CSSProperties = isRtl
    ? { fontSize: '14px', letterSpacing: 'normal', textTransform: 'none' }
    : { fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase' };

  return (
    <div ref={heroRef}>
      {/* Hero Section (unchanged per agreement) */}
      <section
        className="relative flex min-h-[100dvh] flex-col items-center justify-center"
        style={{ backgroundColor: '#173835' }}
      >
        {/* Title */}
        <h1
          ref={titleRef}
          className="hero-title rtl-display-l font-six-caps text-[calc(1rem+20vw)] leading-[0.9] text-white lowercase text-center"
        >
          {(isRtl ? [title] : title.split('')).map((char, i) => (
            <span
              key={i}
              className={`letter inline-block ${char === ' ' ? 'mx-4' : ''}`}
              style={{ transformOrigin: 'center bottom' }}
            >
              {char === ' ' ? '\u00A0' : char}
            </span>
          ))}
        </h1>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="max-w-4xl text-center leading-relaxed text-white/60"
          style={{ marginTop: 'clamp(2.5rem, 6vw, 5rem)', paddingLeft: '2rem', paddingRight: '2rem', fontSize: 'clamp(0.875rem, 1.5vw, 1.25rem)' }}
        >
          {t.hero.subtitle}
        </p>

        {/* Bottom navigation */}
        <div
          className="absolute left-0 right-0"
          style={{ bottom: 'clamp(2rem, 5vw, 3.5rem)', paddingLeft: 'clamp(1.5rem, 4vw, 4rem)', paddingRight: 'clamp(1.5rem, 4vw, 4rem)' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center" style={{ gap: '0.75rem' }}>
              <span className="text-white/40" style={microLabel}>{t.hero.scrollToExplore}</span>
              <svg
                className="text-white/40"
                style={{ width: '14px', height: '14px' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <a
              href="#services"
              className="text-white/40 hover:text-white/70 transition-colors"
              style={microLabel}
            >
              {t.hero.servicesLink}
            </a>
          </div>
        </div>
      </section>

      {/* Services Section: clean white on dark, no gimmicky bg animation */}
      <section
        id="services"
        className="light-section relative"
        style={{ backgroundColor: '#fff', paddingTop: 'clamp(4rem, 8vw, 8rem)', paddingBottom: 'clamp(4rem, 8vw, 8rem)' }}
      >
        <div style={{ maxWidth: '1280px', marginLeft: 'auto', marginRight: 'auto', paddingLeft: 'clamp(1.5rem, 4vw, 4rem)', paddingRight: 'clamp(1.5rem, 4vw, 4rem)' }}>
          <div className="flex flex-col md:flex-row" style={{ gap: 'clamp(2rem, 4vw, 6rem)' }}>
            {/* Left: sticky title */}
            <div className="md:w-1/3">
              <div className="md:sticky" style={{ top: '120px' }}>
                <span className="eyebrow eyebrow--ink" style={{ marginBottom: '1.5rem' }}>
                  {t.services.eyebrow}
                </span>
                <h2 className="rtl-display-m font-six-caps leading-[0.9] text-black uppercase" style={{ fontSize: 'clamp(4rem, 8vw, 7rem)' }}>
                  {t.services.titleLine1}
                  {t.services.titleLine2 && (<><br />{t.services.titleLine2}</>)}
                </h2>
              </div>
            </div>

            {/* Right: accordion list */}
            <div ref={servicesRef} className="md:w-2/3">
              {/* Top border for the first item */}
              <div style={{ borderTop: '1px solid rgba(0,0,0,0.1)' }} />
              {services.map((service, index) => (
                <div
                  key={index}
                  className="service-item"
                  style={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}
                >
                  <button
                    onClick={() => toggleService(index)}
                    className="w-full flex items-center justify-between cursor-pointer"
                    style={{ paddingTop: 'clamp(1rem, 2vw, 1.5rem)', paddingBottom: 'clamp(1rem, 2vw, 1.5rem)' }}
                  >
                    <h3 style={{ color: expandedService === index ? '#000' : 'rgba(0,0,0,0.7)', fontSize: 'clamp(1rem, 1.5vw, 1.25rem)', fontWeight: 500, textAlign: 'start', transition: 'color 0.3s' }}>
                      {service.title}
                    </h3>
                    <div
                      className="flex items-center justify-center transition-transform duration-300"
                      style={{
                        width: '32px',
                        height: '32px',
                        flexShrink: 0,
                        marginInlineStart: '1rem',
                        transform: expandedService === index ? 'rotate(45deg)' : 'rotate(0deg)',
                      }}
                    >
                      <svg
                        style={{ width: '18px', height: '18px', color: expandedService === index ? '#000' : 'rgba(0,0,0,0.3)', transition: 'color 0.3s' }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 5v14m7-7H5" />
                      </svg>
                    </div>
                  </button>
                  {/* Expandable content */}
                  <div
                    className="overflow-hidden transition-all duration-500 ease-out"
                    style={{
                      maxHeight: expandedService === index ? (isRtl ? '360px' : '200px') : '0px',
                      opacity: expandedService === index ? 1 : 0,
                    }}
                  >
                    <p
                      style={{ color: 'rgba(0,0,0,0.55)', fontSize: '15px', lineHeight: '1.7', paddingBottom: '1.5rem', maxWidth: '540px' }}
                    >
                      {service.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section: minimal, editorial */}
      <section style={{ backgroundColor: '#0c0c0c', paddingTop: 'clamp(5rem, 10vw, 10rem)', paddingBottom: 'clamp(5rem, 10vw, 10rem)' }}>
        <div
          ref={missionRef}
          style={{ maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto', paddingLeft: 'clamp(1.5rem, 4vw, 4rem)', paddingRight: 'clamp(1.5rem, 4vw, 4rem)' }}
        >
          <span className="eyebrow" style={{ marginBottom: 'clamp(2rem, 4vw, 3.5rem)' }}>
            {t.mission.eyebrow}
          </span>
          <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', fontWeight: 400, lineHeight: 1.4, color: '#fff', marginBottom: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
            {t.mission.heading}
          </h2>
          <p style={{ fontSize: 'clamp(0.9375rem, 1.2vw, 1.125rem)', lineHeight: 1.8, color: 'rgba(255,255,255,0.5)' }}>
            {t.mission.body}
          </p>
        </div>
      </section>

      {/* Clients Section */}
      <section style={{ backgroundColor: '#0c0c0c', paddingTop: 'clamp(3rem, 6vw, 6rem)', paddingBottom: 'clamp(5rem, 10vw, 10rem)' }}>
        <div style={{ maxWidth: '1280px', marginLeft: 'auto', marginRight: 'auto', paddingLeft: 'clamp(1.5rem, 4vw, 4rem)', paddingRight: 'clamp(1.5rem, 4vw, 4rem)' }}>
          {/* Thin divider */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginBottom: 'clamp(3rem, 6vw, 5rem)' }} />

          {/* Section label */}
          <span className="eyebrow" style={{ marginBottom: 'clamp(2rem, 4vw, 3.5rem)' }}>
            {t.clients.eyebrow}
          </span>

          {/* Heading */}
          <h2
            className="rtl-display-m font-six-caps uppercase"
            style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 0.95, color: '#fff', marginBottom: 'clamp(3rem, 6vw, 5rem)', maxWidth: '700px' }}
          >
            {t.clients.heading}
          </h2>

          {/* Client grid */}
          {loadingClients ? (
            <div className="flex items-center justify-center" style={{ paddingTop: '4rem', paddingBottom: '4rem' }}>
              <div className="w-10 h-10 border-2 border-white/10 border-t-white/40 rounded-full animate-spin" />
            </div>
          ) : clients.length > 0 ? (
            <div
              className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5"
              style={{ gap: '1px', backgroundColor: 'rgba(255,255,255,0.06)' }}
            >
              {clients.map((client, index) => (
                <div key={index} style={{ backgroundColor: '#0c0c0c' }}>
                  {client.url ? (
                    <a
                      href={client.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-center transition-all duration-500 hover:bg-white/[0.03]"
                      style={{ height: 'clamp(5rem, 8vw, 7rem)' }}
                    >
                      <span
                        className="uppercase transition-all duration-500 group-hover:text-white/80"
                        style={{ fontSize: 'clamp(0.6875rem, 1vw, 0.875rem)', fontWeight: 500, color: 'rgba(255,255,255,0.3)', ...clientNameStyle(client.name) }}
                      >
                        {client.name}
                      </span>
                    </a>
                  ) : (
                    <div
                      className="group flex items-center justify-center transition-all duration-500 hover:bg-white/[0.03]"
                      style={{ height: 'clamp(5rem, 8vw, 7rem)' }}
                    >
                      <span
                        className="uppercase transition-all duration-500 group-hover:text-white/60"
                        style={{ fontSize: 'clamp(0.6875rem, 1vw, 0.875rem)', fontWeight: 500, color: 'rgba(255,255,255,0.3)', ...clientNameStyle(client.name) }}
                      >
                        {client.name}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
              <p style={{ color: 'rgba(255,255,255,0.3)' }}>{t.clients.empty}</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section: contact link, compact */}
      <Link
        href={href('/contact')}
        className="block group"
        style={{ backgroundColor: '#173835' }}
      >
        <section
          className="flex flex-col items-center justify-center cursor-pointer"
          style={{ paddingTop: 'clamp(6rem, 12vw, 12rem)', paddingBottom: 'clamp(6rem, 12vw, 12rem)' }}
        >
          {/* Subtitle */}
          <p
            className="uppercase text-center transition-opacity duration-500 group-hover:opacity-80"
            style={{ ...(isRtl ? { fontSize: '14px', letterSpacing: 'normal', textTransform: 'none' as const } : { fontSize: '11px', letterSpacing: '0.25em' }), color: 'rgba(255,255,255,0.4)', marginBottom: 'clamp(1rem, 2vw, 2rem)' }}
          >
            {t.cta.prompt}
          </p>

          {/* Large CONTACT text */}
          <h2
            ref={contactTitleRef}
            className="rtl-display-l font-six-caps text-white lowercase transition-transform duration-500 group-hover:scale-[1.02]"
            style={{ fontSize: 'clamp(5rem, 20vw, 18rem)', lineHeight: 0.85 }}
          >
            {(isRtl ? [t.cta.word] : t.cta.word.split('')).map((char, i) => (
              <span
                key={i}
                className="contact-letter inline-block"
                style={{ transformOrigin: 'center bottom' }}
              >
                {char}
              </span>
            ))}
          </h2>
        </section>
      </Link>
    </div>
  );
}
